import { useTheme } from "@emotion/react";
import { Card, CardContainer } from "../../../../components/Card";
import { StatCard } from "../overview/OverviewHelper";
import { AreaChart, BarChart, LineChart } from "../../../../components/Charts";
import { formatCurrency, formatNumber } from "../utils/StatUtils";

const CollaborationsView = ({ filterBy, filteredData = [] }) => {
    const theme = useTheme();

    const getCumulativeData = () => {
        const cumulative = [];
        let runningTotals = {};

        filteredData.forEach((week = {}) => {
            const cumulativeWeek = { ...week };
            const cumulativeFields = [
                "collabs_viewed",
                "collabs_engaged",
                "collab_conversion",
                "collabs_target",
                "collabs_billed",
                "collabs_closed",
                "collabs_proposed",
            ];
            cumulativeFields.forEach((field) => {
                runningTotals[field] =
                    (runningTotals[field] || 0) + (week?.[field] || 0);
                cumulativeWeek[`${field}_Cumulative`] = runningTotals[field];
            });
            cumulative.push(cumulativeWeek);
        });

        return cumulative;
    };

    const dataToUse =
        filterBy === "cumulativeView" ? getCumulativeData() : filteredData;

    const latest = dataToUse?.length > 0 ? dataToUse[dataToUse.length - 1] : {};

    const totalCollabsViewed = dataToUse.reduce(
        (sum, item) => sum + (item?.collabs_viewed || 0),
        0
    );
    const totalCollabsEngaged = dataToUse.reduce(
        (sum, item) => sum + (item?.collabs_engaged || 0),
        0
    );
    const totalCollabsConversion = dataToUse.reduce(
        (sum, item) => sum + (item?.collabs_engagement_conversion || 0),
        0
    );
    const totalCollabsBilled = dataToUse.reduce(
        (sum, item) => sum + (item?.collabs_billed || 0),
        0
    );

    const stats = [
        {
            key: "collabs_viewed",
            label: "Collabs Viewed",
            value: formatNumber(totalCollabsViewed),
            color: theme?.palette?.graph?.cyan ?? "#00BCD4",
        },
        {
            key: "collabs_engaged",
            label: "Collabs Engaged",
            value: formatNumber(totalCollabsEngaged),
            color: theme?.palette?.graph?.yellow ?? "#FFEB3B",
        },
        {
            key: "collabs_engagement_conversion",
            label: "Collabs Conversion Rate",
            value: `${(totalCollabsConversion || 0).toFixed(1)}%`,
            color: theme?.palette?.graph?.magenta ?? "#E91E63",
        },
        {
            key: "collabs_billed",
            label: "Collabs Billed",
            value: formatCurrency(totalCollabsBilled),
            color: theme?.palette?.graph?.green ?? "#4CAF50",
        },
    ];

    return (
        <div>
            <CardContainer>
                {stats.map((stat, index) => (
                    <Card key={index}>
                        <StatCard
                            value={stat?.value}
                            label={stat?.label}
                            color={stat?.color}
                        />
                    </Card>
                ))}
            </CardContainer>
            <CardContainer>
                <LineChart
                    data={dataToUse}
                    series={
                        filterBy === "weeklyView"
                            ? [
                                  {
                                      name: "Collabs Viewed",
                                      key: "collabs_viewed",
                                  },
                                  {
                                      name: "Collabs Engaged",
                                      key: "collabs_engaged",
                                  },
                              ]
                            : [
                                  {
                                      name: "Collabs Viewed (Cumulative)",
                                      key: "collabs_viewed_Cumulative",
                                  },
                                  {
                                      name: "Collabs Engaged (Cumulative)",
                                      key: "collabs_engaged_Cumulative",
                                  },
                              ]
                    }
                    xKey="week"
                    title="Collaboration Engagement"
                />
                <AreaChart
                    data={dataToUse}
                    series={
                        filterBy === "weeklyView"
                            ? [
                                  {
                                      name: "Collabs Billed",
                                      key: "collabs_billed",
                                  },
                              ]
                            : [
                                  {
                                      name: "Collabs Billed (Cumulative)",
                                      key: "collabs_billed_Cumulative",
                                  },
                              ]
                    }
                    xKey="week"
                    title="Collaboration Revenue Trend"
                />
            </CardContainer>
            <CardContainer>
                <BarChart
                    data={dataToUse}
                    series={
                        filterBy === "weeklyView"
                            ? [
                                  {
                                      name: "Collabs Proposed",
                                      key: "collabs_proposed",
                                  },
                                  {
                                      name: "Collabs Closed",
                                      key: "collabs_closed",
                                  },
                                  {
                                      name: "Collabs Billed",
                                      key: "collabs_billed",
                                  },
                              ]
                            : [
                                  {
                                      name: "Collabs Proposed (Cumulative)",
                                      key: "collabs_proposed_Cumulative",
                                  },
                                  {
                                      name: "Collabs Closed (Cumulative)",
                                      key: "collabs_closed_Cumulative",
                                  },
                                  {
                                      name: "Collabs Billed (Cumulative)",
                                      key: "collabs_billed_Cumulative",
                                  },
                              ]
                    }
                    xKey="week"
                    title="Collaboration Funnel Analysis"
                />
            </CardContainer>
        </div>
    );
};

export default CollaborationsView;
